import confetti from "canvas-confetti";

const GREEN = "#00D37F";
const GOLD = "#FFB703";
const RED = "#FF2D45";
const BLUE = "#007AFF";
const WHITE = "#ffffff";

/* ===== AUDIO (whistle / cheer) ===== */
const ctxRef = { current: null };
function getCtx() {
  if (typeof window === "undefined") return null;
  if (!ctxRef.current) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctxRef.current = new AC();
  }
  if (ctxRef.current.state === "suspended") {
    ctxRef.current.resume().catch(() => {});
  }
  return ctxRef.current;
}

// Real whistle using two oscillators with vibrato and a noise burst
export function whistle() {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime;

  // Sustained whistle tone with vibrato
  const osc = ctx.createOscillator();
  const oscGain = ctx.createGain();
  const vibrato = ctx.createOscillator();
  const vibratoGain = ctx.createGain();
  vibrato.frequency.value = 18;
  vibratoGain.gain.value = 60;
  vibrato.connect(vibratoGain);
  vibratoGain.connect(osc.frequency);
  osc.type = "triangle";
  osc.frequency.setValueAtTime(2200, t0);
  osc.frequency.linearRampToValueAtTime(2700, t0 + 0.05);
  oscGain.gain.setValueAtTime(0, t0);
  oscGain.gain.linearRampToValueAtTime(0.22, t0 + 0.04);
  oscGain.gain.setValueAtTime(0.22, t0 + 0.42);
  oscGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
  osc.connect(oscGain);
  oscGain.connect(ctx.destination);
  vibrato.start(t0);
  osc.start(t0);
  vibrato.stop(t0 + 0.6);
  osc.stop(t0 + 0.6);

  // Air-noise pea burst at start
  const buf = ctx.createBuffer(1, ctx.sampleRate * 0.18, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.05, t0);
  noiseGain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.18);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2500;
  bp.Q.value = 6;
  noise.connect(bp);
  bp.connect(noiseGain);
  noiseGain.connect(ctx.destination);
  noise.start(t0);
}

export function goalHorn() {
  const ctx = getCtx();
  if (!ctx) return;
  const t0 = ctx.currentTime;

  // Stadium air-horn (3 layered sawtooth tones, classic vuvuzela vibe)
  [233, 277, 349].forEach((freq, i) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(freq, t0);
    o.frequency.linearRampToValueAtTime(freq * 1.05, t0 + 0.9);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(0.13 - i * 0.02, t0 + 0.06);
    g.gain.setValueAtTime(0.13 - i * 0.02, t0 + 0.85);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.25);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 120;
    o.connect(hp);
    hp.connect(g);
    g.connect(ctx.destination);
    o.start(t0);
    o.stop(t0 + 1.3);
  });

  // Crowd cheer = filtered pink noise with slow envelope
  const buf = ctx.createBuffer(1, ctx.sampleRate * 1.6, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let b0 = 0,
    b1 = 0,
    b2 = 0;
  for (let i = 0; i < data.length; i++) {
    const w = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + w * 0.099046;
    b1 = 0.96300 * b1 + w * 0.2965164;
    b2 = 0.57000 * b2 + w * 1.0526913;
    data[i] = (b0 + b1 + b2 + w * 0.1848) * 0.18;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0, t0);
  ng.gain.linearRampToValueAtTime(0.32, t0 + 0.15);
  ng.gain.linearRampToValueAtTime(0.28, t0 + 1.0);
  ng.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.7);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 1200;
  bp.Q.value = 0.9;
  noise.connect(bp);
  bp.connect(ng);
  ng.connect(ctx.destination);
  noise.start(t0);

  // Whistle on top to complete the goal vibe
  setTimeout(() => whistle(), 60);
}

/* ===== EMOJI BURST ===== */
function emojiBurst(emojis = ["⚽", "🎉", "🇲🇽", "🇺🇸", "🇨🇦"], origin = { x: 0.5, y: 0.6 }) {
  if (typeof document === "undefined") return;
  const root = document.body;
  const container = document.createElement("div");
  container.style.cssText = `
    position: fixed; inset: 0; pointer-events: none; z-index: 9999;
  `;
  root.appendChild(container);
  const count = emojis.length * 6;
  for (let i = 0; i < count; i++) {
    const em = document.createElement("div");
    em.textContent = emojis[i % emojis.length];
    const left = origin.x * 100 + (Math.random() - 0.5) * 30;
    const top = origin.y * 100;
    const dx = (Math.random() - 0.5) * 600;
    const dy = -200 - Math.random() * 320;
    const rot = (Math.random() - 0.5) * 540;
    const size = 22 + Math.random() * 22;
    em.style.cssText = `
      position: absolute; left: ${left}%; top: ${top}%;
      font-size: ${size}px; transition: transform 1.4s cubic-bezier(.2,.7,.2,1), opacity 1.4s;
      transform: translate(0,0) rotate(0deg); opacity: 1;
      filter: drop-shadow(0 6px 8px rgba(0,0,0,0.4));
      will-change: transform;
    `;
    container.appendChild(em);
    requestAnimationFrame(() => {
      em.style.transform = `translate(${dx}px, ${dy}px) rotate(${rot}deg)`;
      em.style.opacity = "0";
    });
  }
  setTimeout(() => container.remove(), 1600);
}

/* ===== PUBLIC EFFECTS ===== */

// When user saves a prediction (slip submitted)
export function celebratePredictionSaved() {
  whistle();
  emojiBurst(["⚽", "📋", "✏️", "🎯", "🟢"], { x: 0.5, y: 0.55 });
  confetti({
    particleCount: 60,
    angle: 90,
    spread: 75,
    startVelocity: 38,
    origin: { y: 0.6 },
    colors: [GREEN, WHITE, GOLD],
    scalar: 0.9,
    ticks: 90,
  });
}

// When the user nails the EXACT score
export function celebrateExactWin() {
  goalHorn();
  emojiBurst(["⚽", "🥅", "🏆", "🎉", "🔥", "🇲🇽", "🇺🇸", "🇨🇦"], { x: 0.5, y: 0.5 });
  // Side cannons + center burst
  const fire = (originX, angle) => {
    confetti({
      particleCount: 80,
      angle,
      spread: 70,
      startVelocity: 55,
      origin: { x: originX, y: 0.7 },
      colors: [GREEN, GOLD, WHITE, BLUE],
      scalar: 1.1,
      ticks: 200,
    });
  };
  fire(0.05, 60);
  fire(0.95, 120);
  setTimeout(
    () =>
      confetti({
        particleCount: 200,
        spread: 360,
        startVelocity: 30,
        origin: { y: 0.5 },
        colors: [GREEN, GOLD, RED, BLUE, WHITE],
        scalar: 1.2,
        ticks: 250,
        shapes: ["circle", "square"],
      }),
    220
  );
}

// When the user gets only the outcome (2pt win)
export function celebrateOutcomeWin() {
  whistle();
  emojiBurst(["⚽", "✅", "🏟️"], { x: 0.5, y: 0.55 });
  confetti({
    particleCount: 100,
    spread: 100,
    startVelocity: 42,
    origin: { y: 0.6 },
    colors: [GOLD, WHITE, GREEN],
    scalar: 1,
    ticks: 150,
  });
}
